import { useState, useEffect, useReducer } from 'react';
import { QueryClient } from 'react-query';
import { useClerk, useUser } from '@clerk/clerk-react';
import { GlobalReducer } from '../reducer/GlobalReducer';
import axios from 'axios';

export function useAppInitialization() {
  const [loaderActive, setLoaderActive] = useState(false);
  const [token, setToken] = useState<string>('');
  const [queryClient] = useState(() => new QueryClient());
  const { isSignedIn, isLoaded } = useUser();
  const { session, redirectToSignIn, redirectToSignUp } = useClerk();

  const [state, dispatch] = useReducer(GlobalReducer, {
    notification: {
      auth: {
        login: false,
        reset: false,
      },
    },
  });

  // Set up Axios interceptors
  useEffect(() => {
    const interceptor = axios.interceptors.request.use(async (config) => {
      const isDev = process.env.NODE_ENV === 'development';
      const token = await session?.getToken(
        isDev ? { template: 'redstonehr_template' } : undefined
      );
      
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
      return config;
    });
    return () => {
      axios.interceptors.request.eject(interceptor);
    };
  }, [session]);

  // Handle authentication and loading states
  useEffect(() => {
    async function checkUser() {
      if (!isLoaded) {
        setLoaderActive(true);
      } else if (!isSignedIn) {
        // The marketing site's "Start free trial" links to /sign-up.
        if (window.location.pathname === '/sign-up') {
          await redirectToSignUp();
        } else {
          await redirectToSignIn();
        }
        setLoaderActive(true);
      } else {
        setLoaderActive(false);
      }
    }

    checkUser();
  }, [isLoaded, isSignedIn, redirectToSignIn, redirectToSignUp]);

  // Get the access token
  useEffect(() => {
    const getAccessToken = async () => {
      const token = await session?.getToken();
      if (token) {
        setToken(token);
      }
    };

    if (isSignedIn) {
      getAccessToken();
    }
  }, [session, isSignedIn]);

  return { loaderActive, queryClient, state, dispatch, token };
}
